// Shop service for buying and selling items

import shopData from './shop.data.json';
import inventoryData from './inventory.data.json';
import { inventoryService } from './inventory.service.js';

class ShopService {
  constructor() {
    this.shopStates = this.loadShopStates();
    this.initializeShops();
    this.listeners = []; // Para notificar cambios
    this.setupAutoUpdate();
  }

  // Initialize shops with default inventory
  initializeShops() {
    for (const [location, shopConfig] of Object.entries(shopData.shops)) {
      if (!this.shopStates[location]) {
        this.shopStates[location] = {
          lastRestock: Date.now(),
          currentInventory: { ...shopConfig.inventory },
          priceModifiers: {},
          dailySeed: this.getDailySeed()
        };
      }
    }
    this.saveShopStates();
  }

  // Setup auto-update system
  setupAutoUpdate() {
    // Check for updates every 30 seconds (sync with app store)
    this.updateInterval = setInterval(() => {
      this.checkForUpdates();
    }, 30000);
    
    // Also check daily for price changes
    this.dailyCheckInterval = setInterval(() => {
      this.checkDailyChanges();
    }, 60000); // Check every minute for daily changes
  }

  // Check for any shop updates
  checkForUpdates() {
    let hasChanges = false;
    
    // Check each shop for restock
    for (const location of Object.keys(shopData.shops)) {
      const oldState = JSON.stringify(this.shopStates[location]);
      this.updateShopInventory(location);
      const newState = JSON.stringify(this.shopStates[location]);
      
      if (oldState !== newState) {
        hasChanges = true;
        console.log(`Shop updated: ${location}`);
      }
    }
    
    // Notify listeners if there are changes
    if (hasChanges) {
      this.notifyListeners();
    }
  }

  // Check for daily price changes
  checkDailyChanges() {
    const currentSeed = this.getDailySeed();
    let priceChanged = false;
    
    for (const location of Object.keys(this.shopStates)) {
      if (this.shopStates[location].dailySeed !== currentSeed) {
        this.shopStates[location].dailySeed = currentSeed;
        priceChanged = true;
      }
    }
    
    if (priceChanged) {
      this.saveShopStates();
      this.notifyListeners();
      console.log('Daily price fluctuation updated');
    }
  }

  // Add listener for shop updates
  onUpdate(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  // Notify all listeners of changes
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in shop update listener:', error);
      }
    });
  }

  // Get shop for location
  getShop(location) {
    const shopConfig = shopData.shops[location];
    if (!shopConfig) {
      throw new Error(`No shop found for location: ${location}`);
    }

    const shopState = this.shopStates[location];
    this.updateShopInventory(location);

    return {
      ...shopConfig,
      currentInventory: shopState.currentInventory,
      lastRestock: shopState.lastRestock
    };
  }

  // Get available items for purchase
  getShopInventory(location, filter = {}) {
    const shop = this.getShop(location);
    const items = [];

    for (const [itemId, shopItem] of Object.entries(shop.currentInventory)) {
      const itemData = inventoryData.items[itemId];
      if (!itemData || shopItem.stock <= 0) continue;

      // Apply filters
      if (filter.category && itemData.category !== filter.category) continue;
      if (filter.rarity && itemData.rarity !== filter.rarity) continue;
      if (filter.type && itemData.type !== filter.type) continue;

      const price = this.calculateBuyPrice(location, itemId, shopItem.basePrice);
      
      items.push({
        ...itemData,
        shopStock: shopItem.stock,
        price,
        originalPrice: shopItem.basePrice,
        priceChange: price !== shopItem.basePrice
      });
    }

    return items.sort((a, b) => a.name.localeCompare(b.name));
  }

  // Buy item from shop
  buyItem(location, itemId, quantity = 1) {
    const shop = this.getShop(location);
    const shopState = this.shopStates[location];
    const shopItem = shopState.currentInventory[itemId];
    const itemData = inventoryData.items[itemId];

    if (!shopItem || !itemData) {
      throw new Error(`Item ${itemId} not available in shop`);
    }

    if (shopItem.stock < quantity) {
      throw new Error(`Not enough stock. Available: ${shopItem.stock}`);
    }

    const unitPrice = this.calculateBuyPrice(location, itemId, shopItem.basePrice);
    const totalPrice = unitPrice * quantity;

    // Check if player has enough money (this should be validated by the component)
    // We'll return the transaction details for the component to handle

    const transaction = {
      itemId,
      itemName: itemData.name,
      quantity,
      unitPrice,
      totalPrice,
      newStock: shopItem.stock - quantity
    };

    // Update shop stock
    shopState.currentInventory[itemId].stock -= quantity;
    this.saveShopStates();

    return transaction;
  }

  // Sell item to shop
  sellItem(location, itemId, quantity = 1) {
    const shop = this.getShop(location);
    const itemData = inventoryData.items[itemId];

    if (!itemData) {
      throw new Error(`Unknown item: ${itemId}`);
    }

    // Check if shop accepts this item
    const acceptsItem = shop.acceptsItems.includes('all') || 
                       shop.acceptsItems.includes(itemId) ||
                       shop.acceptsItems.includes(itemData.category);

    if (!acceptsItem) {
      throw new Error(`${shop.name} doesn't buy ${itemData.name}`);
    }

    const unitPrice = this.calculateSellPrice(location, itemId, itemData.value);
    const totalPrice = unitPrice * quantity;

    const transaction = {
      itemId,
      itemName: itemData.name,
      quantity,
      unitPrice,
      totalPrice
    };

    return transaction;
  }

  // Calculate buy price with modifiers
  calculateBuyPrice(location, itemId, basePrice) {
    const shop = shopData.shops[location];
    let price = basePrice * shop.buyPriceMultiplier;

    // Apply daily price fluctuation
    if (shopData.priceFluctuation.enabled) {
      const fluctuation = this.getDailyPriceFluctuation(itemId);
      price *= fluctuation;
    }

    return Math.ceil(price);
  }

  // Calculate sell price with modifiers  
  calculateSellPrice(location, itemId, baseValue) {
    const shop = shopData.shops[location];
    let price = baseValue * shop.sellPriceMultiplier;

    // Apply daily price fluctuation (inverse for selling)
    if (shopData.priceFluctuation.enabled) {
      const fluctuation = this.getDailyPriceFluctuation(itemId);
      price *= (2 - fluctuation); // Inverse relationship
    }

    return Math.floor(price);
  }

  // Get daily price fluctuation based on item and date
  getDailyPriceFluctuation(itemId) {
    const seed = this.getDailySeed();
    const itemSeed = this.hashString(itemId);
    const combinedSeed = (seed + itemSeed) % 1000;
    
    const randomValue = (combinedSeed / 1000);
    const { minMultiplier, maxMultiplier } = shopData.priceFluctuation;
    
    return minMultiplier + (randomValue * (maxMultiplier - minMultiplier));
  }

  // Get daily seed for consistent daily prices
  getDailySeed() {
    const today = new Date();
    const dateString = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
    return this.hashString(dateString);
  }

  // Simple hash function for generating seeds
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  // Update shop inventory (restock)
  updateShopInventory(location) {
    const shopConfig = shopData.shops[location];
    const shopState = this.shopStates[location];
    const now = Date.now();
    
    // Check if it's time to restock (every 6 hours)
    const restockInterval = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
    const timeSinceRestock = now - shopState.lastRestock;
    
    if (timeSinceRestock >= restockInterval) {
      this.restockShop(location);
      shopState.lastRestock = now;
      this.saveShopStates();
    }
  }

  // Restock shop inventory
  restockShop(location) {
    const shopConfig = shopData.shops[location];
    const shopState = this.shopStates[location];
    
    for (const [itemId, shopItem] of Object.entries(shopConfig.inventory)) {
      const currentStock = shopState.currentInventory[itemId]?.stock || 0;
      const maxStock = shopItem.stock;
      const restockAmount = shopItem.restockRate;
      
      if (currentStock < maxStock && restockAmount > 0) {
        const newStock = Math.min(maxStock, currentStock + restockAmount);
        shopState.currentInventory[itemId] = {
          ...shopItem,
          stock: newStock
        };
      }
    }
    
    console.log(`Shop in ${location} has been restocked`);
  }

  // Get shop categories for filtering
  getShopCategories(location) {
    const shop = shopData.shops[location];
    return shop?.categories || [];
  }

  // Get all available rarities in shop
  getShopRarities(location) {
    const items = this.getShopInventory(location);
    const rarities = new Set(items.map(item => item.rarity));
    return Array.from(rarities).sort();
  }

  // Get all available types in shop
  getShopTypes(location) {
    const items = this.getShopInventory(location);
    const types = new Set(items.map(item => item.type));
    return Array.from(types).sort();
  }

  // Check if player can afford item
  canAfford(playerMoney, price, quantity = 1) {
    return playerMoney >= (price * quantity);
  }

  // Get shop info for display
  getShopInfo(location) {
    const shop = shopData.shops[location];
    if (!shop) return null;

    const shopState = this.shopStates[location];
    const timeSinceRestock = Date.now() - shopState.lastRestock;
    const restockInterval = 6 * 60 * 60 * 1000; // 6 hours
    const timeUntilRestock = Math.max(0, restockInterval - timeSinceRestock);

    return {
      name: shop.name,
      description: shop.description,
      keeper: shop.keeper,
      icon: shop.icon,
      timeUntilRestock: timeUntilRestock,
      restockHours: Math.ceil(timeUntilRestock / (60 * 60 * 1000))
    };
  }

  // Save/load shop states
  saveShopStates() {
    try {
      localStorage.setItem('shop:states', JSON.stringify(this.shopStates));
    } catch (error) {
      console.error('Failed to save shop states:', error);
    }
  }

  loadShopStates() {
    try {
      const saved = localStorage.getItem('shop:states');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load shop states:', error);
    }
    return {};
  }

  // Cleanup intervals when service is destroyed
  destroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.dailyCheckInterval) {
      clearInterval(this.dailyCheckInterval);
      this.dailyCheckInterval = null;
    }
    this.listeners = [];
  }
}

// Export singleton instance
export const shopService = new ShopService();